'use client';

import i18next, { createInstance } from 'i18next';
import HttpBackend from 'i18next-http-backend';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE_NAME,
  type AppLocale,
} from './shared';
import type { TranslationKey } from './generated/translation-keys';

type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (nextLocale: AppLocale) => void;
  ready: boolean;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

let initPromise: Promise<void> | null = null;

// The public landing is server-rendered. Initialize its own i18next instance
// synchronously with the request's translations: a browser-only effect cannot
// initialize translations while React renders the initial HTML on the server.
// A per-provider instance also prevents one request's locale leaking into another.
function createServerReadyI18n(locale: AppLocale, translations: Record<string, unknown>) {
  const instance = createInstance();
  void instance.use(HttpBackend).use(initReactI18next).init({
    lng: locale,
    fallbackLng: locale,
    supportedLngs: ['vi', 'en'],
    ns: ['translation'],
    defaultNS: 'translation',
    resources: {
      [locale]: { translation: translations }
    },
    partialBundledLanguages: true,
    initAsync: false,
    interpolation: { escapeValue: false },
    backend: { loadPath: '/locale/{{lng}}.json' },
    react: { useSuspense: false }
  });
  return instance;
}

function ensureI18n(initialLocale: AppLocale) {
  if (!initPromise) {
    initPromise = i18next
      .use(HttpBackend)
      .use(initReactI18next)
      .init({
        lng: initialLocale,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: ['vi', 'en'],
        ns: ['translation'],
        defaultNS: 'translation',
        interpolation: {
          escapeValue: false
        },
        backend: {
          loadPath: '/locale/{{lng}}.json'
        },
        react: {
          useSuspense: false
        }
      })
      .then(() => undefined);
  }

  return initPromise;
}

// The locale lives in a cookie only, so the server renders the same language the client uses.
function persistLocale(locale: AppLocale) {
  if (typeof document !== 'undefined') {
    document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; path=/; max-age=31536000; samesite=lax`;
    document.documentElement.lang = locale;
  }
}

export function I18nProvider({
  children,
  initialLocale,
  initialTranslations
}: {
  children: ReactNode;
  initialLocale: AppLocale;
  /** Server-provided strings for SSR routes; other app routes keep their lazy-loaded behavior. */
  initialTranslations?: Record<string, unknown>;
}) {
  const [locale, setLocaleState] = useState<AppLocale>(initialLocale);
  const [localeI18n] = useState(() =>
    initialTranslations ? createServerReadyI18n(initialLocale, initialTranslations) : i18next
  );
  const [ready, setReady] = useState(Boolean(initialTranslations));
  const didResolveInitialLocale = useRef(false);

  useEffect(() => {
    if (didResolveInitialLocale.current) {
      return;
    }

    didResolveInitialLocale.current = true;
    if (initialTranslations) {
      // The SSR instance already has the correct language before first render.
      persistLocale(initialLocale);
      return;
    }

    // initialLocale already comes from the locale cookie (or Accept-Language) on the server.
    const nextLocale = initialLocale;

    void ensureI18n(nextLocale).then(async () => {
      if (i18next.language !== nextLocale) {
        await i18next.changeLanguage(nextLocale);
      }
      persistLocale(nextLocale);
      setReady(true);
    });
  }, [initialLocale, initialTranslations]);

  useEffect(() => {
    if (initialTranslations) {
      void localeI18n.changeLanguage(locale).then(() => {
        persistLocale(locale);
        setReady(true);
      });
      return;
    }
    void ensureI18n(locale).then(async () => {
      if (i18next.language !== locale) {
        await i18next.changeLanguage(locale);
      }
      persistLocale(locale);
      setReady(true);
    });
  }, [locale, initialTranslations, localeI18n]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      ready,
      setLocale: (nextLocale: AppLocale) => {
        setLocaleState(nextLocale);
      }
    }),
    [locale, ready]
  );

  return (
    <LocaleContext.Provider value={value}>
      <I18nextProvider i18n={localeI18n}>{children}</I18nextProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error('useLocale must be used inside I18nProvider');
  }

  return context;
}

export function useAppTranslation() {
  const translation = useTranslation();
  const { t: rawT } = translation;

  const t = useCallback(
    (key: TranslationKey, options?: Record<string, unknown>) =>
      rawT(key, options as never) as unknown as string,
    [rawT]
  );

  return useMemo(
    () => ({
      ...translation,
      t
    }),
    [t, translation]
  );
}
